"use client";

import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SortableHeader } from "@/components/sortable_header";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { PizzaDTO, PizzaEvaluationMetadata } from "@/api/custom-schema";
import DetectedComponentsTinyView from "@/components/evaluation/detected-components-tiny-view";
import { useBranchName } from "@/hooks/use-branch-name";
import { StatusBadge } from "@/components/status-badge";
import { PizzaThumbnail } from "@/components/pizza-thumbnail";
import { formatCaptureCount } from "@/lib/counter-format";
import { parsePizzaName } from "@/lib/order-items";
import { copyToClipboard } from "@/lib/clipboard";
import { formatDateTime, formatTime, shortDateTime } from "@/lib/date-format";
import { getIntlLocale, translate } from "@/i18n/translate";
import { useLocalization } from "@/context/localization-context";
import { VerdictCell } from "@/components/verdict-cell";
import { CheckStrip, CheckStripHeader } from "@/components/check-strip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/** Thumbnail cropped to the pizza; hovering it shows a bigger one (no need to open the detail). */
const PizzaThumbnailCell = ({ pizza }: { pizza: PizzaDTO }) => (
  <Tooltip delayDuration={300}>
    <TooltipTrigger asChild>
      <div className="w-fit">
        <PizzaThumbnail
          pizzaId={pizza.id}
          roi={pizza.evaluation?.metadata?.pizza_roi}
          scale={6}
          className="h-14 w-14 rounded-lg"
        />
      </div>
    </TooltipTrigger>
    <TooltipContent side="right" className="border bg-card p-1 shadow-2xl">
      <PizzaThumbnail
        pizzaId={pizza.id}
        roi={pizza.evaluation?.metadata?.pizza_roi}
        scale={12}
        className="h-60 w-60 rounded"
      />
    </TooltipContent>
  </Tooltip>
);

/** Type number and name, with the branch and a re-capture under it. */
const PizzaNameCell = ({ pizza }: { pizza: PizzaDTO }) => {
  const { typeId, name } = parsePizzaName(pizza.name);
  const branch = useBranchName(pizza.branch_id);
  const { t } = useLocalization();
  const recaptured = (pizza.capture_count ?? 0) > 1;
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md bg-muted px-1.5 text-xs font-bold tabular-nums">
        {typeId ?? "–"}
      </span>
      <div className="min-w-0">
        <div className="max-w-52 truncate text-sm font-medium" title={pizza.name}>
          {name}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="truncate">{branch ?? "–"}</span>
          {recaptured && (
            <span className="shrink-0 rounded border px-1 text-[10px] leading-4" title={t("pizzas.cells.recapturedTitle")}>
              {t("pizzas.cells.capturedTimes", { count: pizza.capture_count ?? 0 })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const PizzaBranchCell = ({ branchId }: { branchId: string | undefined }) => {
  const name = useBranchName(branchId);
  return <span className="whitespace-nowrap text-sm">{name || "-"}</span>;
};

const PizzaOrderIdCell = ({ orderId }: { orderId: string }) => {
  const navigate = useNavigate();
  return (
    <Button
      variant="link"
      className="h-auto p-0 font-mono text-xs font-normal text-foreground underline decoration-dotted underline-offset-2 hover:text-muted-foreground"
      title={orderId}
      onClick={(event) => {
        event.preventDefault();
        navigate(`/orders/${orderId}`);
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }}
    >
      {orderId.slice(0, 8)}…
    </Button>
  );
};

// a passed check is grey text, only a failed one is a red pill: the eye goes to the problems
const statusCell = (status: string | null | undefined) =>
  status ? <StatusBadge status={status} quiet /> : null;

export const columns: (
  getLink: (path: string) => string,
) => ColumnDef<PizzaDTO>[] = () => [
  {
    id: "photo",
    header: () => <span className="sr-only">{translate("pizzas.columns.photo")}</span>,
    cell: ({ row }) => <PizzaThumbnailCell pizza={row.original} />,
    enableSorting: false,
  },
  {
    accessorKey: "id",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.id")} />,
    cell: (info) => {
      const id = info.getValue() as string;
      return (
        <span className="font-mono text-xs text-muted-foreground" title={id}>
          {id.slice(0, 8)}
        </span>
      );
    },
  },
  {
    accessorKey: "branch_id",
    header: ({ column }) => <SortableHeader column={column} label={translate("common.branch")} />,
    cell: (info) => {
      const branchId = info.getValue() as string | undefined;
      return <PizzaBranchCell branchId={branchId} />;
    },
  },
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column} label={translate("common.name")} />,
    cell: (info) => <span className="whitespace-nowrap">{info.getValue() as string}</span>,
  },
  {
    displayName: translate("pizzas.columns.orderId"),
    accessorKey: "order_id",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.orderId")} />,
    filter: true,
    cell: (info) => {
      const orderId = info.getValue() as string | null;
      return orderId ? <PizzaOrderIdCell orderId={String(orderId)} /> : null;
    },
  },
  {
    accessorKey: "capture_count",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.captures")} />,
    enableSorting: true,
    cell: (info) => (
      <span className="font-mono tabular-nums">
        {formatCaptureCount(info.getValue() as number | null | undefined)}
      </span>
    ),
  },
  {
    accessorKey: "capture_at",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.capturedAt")} />,
    enableSorting: true,
    sortingFn: "datetime",
    cell: (info) => {
      const value = info.getValue() as string;
      // inside a day group (list sorted by time) the date is in the group header
      const grouped = (info.table.options.meta as { groupedByDay?: boolean } | undefined)?.groupedByDay;
      return (
        <span className="whitespace-nowrap tabular-nums" title={value ? formatDateTime(value) : undefined}>
          {grouped && value ? formatTime(value) : shortDateTime(value)}
        </span>
      );
    },
  },
  {
    id: "pizza_type",
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column} label={translate("common.pizza")} />,
    cell: ({ row }) => <PizzaNameCell pizza={row.original} />,
  },
  {
    id: "evaluation.metadata.overall_evaluation",
    accessorKey: "evaluation.metadata.overall_evaluation.status",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.verdict")} />,
    cell: ({ row }) => <VerdictCell metadata={row.original.evaluation?.metadata} />,
  },
  {
    id: "checks",
    // narrower lists (tablets) get the smaller strip so that the table fits without scrolling sideways
    header: ({ table }) => (
      <CheckStripHeader
        size={(table.options.meta as { compactChecks?: boolean } | undefined)?.compactChecks ? "sm" : "md"}
      />
    ),
    cell: ({ row, table }) => (
      <CheckStrip
        metadata={row.original.evaluation?.metadata}
        size={(table.options.meta as { compactChecks?: boolean } | undefined)?.compactChecks ? "sm" : "md"}
      />
    ),
    enableSorting: false,
  },
  {
    id: "evaluation.metadata.pizza_detection",
    accessorKey: "evaluation.metadata.classification",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.detection")} />,
    cell: (info) => {
      const classification = info.getValue() as string | null;
      if (!classification) return null;
      const isPizza = classification.toLowerCase() !== "no pizza";
      return (
        <StatusBadge
          status={isPizza ? "OK" : "NOK"}
          label={isPizza ? translate("common.pizza") : translate("status.noPizza")}
          quiet
        />
      );
    },
  },
  {
    id: "evaluation.metadata.components_bbs",
    accessorKey: "evaluation.metadata",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.components")} />,
    cell: ({ row, getValue }) => {
      if (row.original.evaluation?.metadata?.classification?.toLowerCase() === "no pizza") {
        return;
      }
      return <DetectedComponentsTinyView metadata={getValue() as PizzaEvaluationMetadata} />;
    },
  },
  {
    id: "evaluation.metadata.components_evaluation.base_components",
    accessorKey: "evaluation.metadata.components_evaluation.base_components.status",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.ingredients")} />,
    cell: (info) => statusCell(info.getValue() as string | null),
  },
  {
    id: "evaluation.metadata.components_evaluation.extras",
    accessorKey: "evaluation.metadata.components_evaluation.extras.status",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.extras")} />,
    cell: (info) => statusCell(info.getValue() as string | null),
  },
  {
    id: "evaluation.metadata.components_evaluation.removals",
    accessorKey: "evaluation.metadata.components_evaluation.removals.status",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.removals")} />,
    cell: (info) => statusCell(info.getValue() as string | null),
  },
  {
    id: "evaluation.metadata.confidence",
    accessorKey: "evaluation.metadata.confidence",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.confidence")} />,
    cell: (info) => {
      const confidence = info.getValue() as number | null;
      if (!confidence) return null;
      const percentage = confidence * 100;
      return (
        <span className={percentage < 70 ? "tabular-nums text-amber-300" : "tabular-nums text-muted-foreground"}>
          {new Intl.NumberFormat(getIntlLocale(), {
            style: "percent",
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }).format(confidence)}
        </span>
      );
    },
  },
  {
    id: "evaluation.metadata.component_count",
    accessorKey: "evaluation.metadata.component_count",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.componentCount")} />,
    cell: (info) => {
      const count = info.getValue() as number | null;
      if (count === null) return null;
      return <span className="tabular-nums">{count}</span>;
    },
  },
  {
    id: "evaluation.metadata.doneness_evaluation",
    accessorKey: "evaluation.metadata.doneness_evaluation",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.doneness")} />,
    cell: (info) => {
      const doneness = info.getValue() as PizzaEvaluationMetadata["doneness_evaluation"];
      if (!doneness) return null;

      const issues: string[] = [];
      if (doneness.burnt?.status) issues.push(translate("pizzas.cells.burnt"));
      if (doneness.raw?.status) issues.push(translate("pizzas.cells.raw"));

      if (issues.length === 0) return <StatusBadge status="OK" quiet />;
      return (
        <div className="flex gap-1">
          {issues.map((issue) => (
            <StatusBadge key={issue} status="NOK" label={issue} />
          ))}
        </div>
      );
    },
  },
  {
    id: "evaluation.metadata.pizza_saver_evaluation",
    accessorKey: "evaluation.metadata.pizza_saver_evaluation.status",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.pizzaSaver")} />,
    cell: (info) => statusCell(info.getValue() as string | null),
  },
  {
    id: "evaluation.metadata.photo_quality_evaluation",
    accessorKey: "evaluation.metadata.photo_quality_evaluation.status",
    header: ({ column }) => <SortableHeader column={column} label={translate("pizzas.columns.photoQualityShort")} />,
    cell: (info) => statusCell(info.getValue() as string | null),
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const pizza = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{translate("pizzas.actions.openMenu")}</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{translate("common.actions")}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => copyToClipboard(pizza.id, translate("pizzas.columns.pizzaId"))}>
              {translate("pizzas.actions.copyId")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => copyToClipboard(JSON.stringify(pizza), translate("pizzas.actions.pizzaData"))}
            >
              {translate("pizzas.actions.copyData")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
